import { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { get, isNil } from 'lodash';
import { useFormikContext } from 'formik';
// Import Types
import type { PartnerCustomField } from 'types';
import type { CustomFieldGroup } from './../types';
//  Import UI Components
import { Dropdown, ExtendedInput, Col } from '@geneui/components';
//  Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import { l, DynamicInputTypes, FieldTypes } from 'constants/common';

interface Props {
    colSize?: number;
    customFields: PartnerCustomField[];
    groupFieldName?: string;
    disabled?: boolean;
}

const StepperCustomFields: FC<Props> = ({
    customFields,
    colSize = 3,
    groupFieldName = FieldTypes.CustomField,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [dropDownSettings, setDropDownSettings] = useState();
    const { doPostRequest } = useRequest();

    const {
        values: formValues,
        submitCount,
        handleBlur,
        handleChange,
        errors,
        touched,
        setFieldValue,
    } = useFormikContext<{ [key: string]: CustomFieldGroup }>();

    const values = formValues[groupFieldName];
    const isSubmitted = submitCount > 0;

    const { getCustomFieldOptionsRequest } = useCancelableRequests({
        getCustomFieldOptionsRequest: UtilsHttpService.getCustomFieldOptions,
    });

    const handleDropdownChange = (fieldName: string) => (data: { value: number | null }) =>
        setFieldValue(fieldName, data?.value ?? null);

    const renderDynamicInputElement = (customField: PartnerCustomField, index: number) => {
        const fieldName = `${groupFieldName}.id_${customField.CustomFieldId}`;
        const value = values[`id_${customField.CustomFieldId}`];
        const error = get(errors, fieldName);
        const isTouched = get(touched, fieldName);

        return (
            <Col key={index} size={colSize}>
                <ExtendedInput
                    title={t(customField.Name)}
                    label={t(customField.Name)}
                    placeholder={t(customField.Name)}
                    labelAppearance="swap"
                    type="text"
                    name={fieldName}
                    isValid={!(isTouched || isSubmitted) || !error}
                    errorText={error}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={value}
                    disabled={disabled}
                />
            </Col>
        );
    };

    const renderDynamicDropDownElement = (customField: PartnerCustomField, index: number) => {
        const fieldName = `${groupFieldName}.id_${customField.CustomFieldId}`;
        const value = values[`id_${customField.CustomFieldId}`];
        const error = get(errors, fieldName);
        const isTouched = get(touched, fieldName);

        return (
            <Col key={index} size={colSize}>
                <Dropdown
                    placeholder={t(customField.Name)}
                    labelAppearance="swap"
                    isMultiSelect={false}
                    disabled={disabled}
                    hasSearch={true}
                    inputSize="default"
                    appearance="outline"
                    data={dropDownSettings?.[index] ?? []}
                    isLoading={isLoading}
                    searchPlaceholderText={t(l.Search)}
                    noDataText={t(l.NoDataFound)}
                    value={+value}
                    onChange={handleDropdownChange(fieldName)}
                    clearable={!customField.IsRequired}
                    isValid={!(isTouched || isSubmitted) || !error}
                    errorText={error}
                    name={fieldName}
                    onBlur={() => {
                        handleBlur({ target: { name: fieldName } });
                    }}
                />
            </Col>
        );
    };

    const renderDynamicInputs = () => {
        return customFields.map((data, index) => {
            switch (data.Type) {
                case DynamicInputTypes.INPUT:
                    return renderDynamicInputElement(data, index);
                case DynamicInputTypes.DROP_DOWN:
                    return renderDynamicDropDownElement(data, index);
                default:
                    // for not supported types
                    return <span key={index}></span>;
            }
        });
    };

    const getInputSettings = () => {
        return customFields.map((customField) => {
            if (customField.Type === DynamicInputTypes.DROP_DOWN) {
                const { CustomFieldId } = customField;
                return doPostRequest(getCustomFieldOptionsRequest.request, {
                    requestBody: [CustomFieldId],
                });
            }
            return null;
        });
    };

    const mapDropDownSettings = (responses) => {
        // for promise all need has error but case dows not cover
        let hasError = false;
        const dropDownData = responses.map((response) => {
            // for cases when promise is null
            if (isNil(response)) return response;

            const { HasError, Data } = response;
            if (!HasError) {
                return Data.map((row) => ({ label: row.OptionValue, value: row.CustomFieldOptionId }));
            }
            // case when one of one promise has error
            hasError = true;
            return null;
        });
        return { Data: dropDownData, HasError: hasError };
    };

    useEffect(() => {
        Promise.all(getInputSettings()).then((responses) => {
            const { Data } = mapDropDownSettings(responses);

            setDropDownSettings(Data);
            setIsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customFields]);

    return <>{renderDynamicInputs()}</>;
};

export default memo(StepperCustomFields);
